// Chay ca 6 buoc noi tiep trong MOT lan chay.
//
// Tham so cua tung buoc van dung chung ParamForm nhu khi chay le, chi khac la
// gom lai thanh tung khoi gap/mo duoc. Bo tich buoc nao thi buoc do bi bo qua.

import { useState } from "react";
import ParamForm, { macDinh, type ParamValues } from "./ParamForm";
import { Alert, Badge, Switch } from "./ui";
import type { Health, Step } from "../types";

export interface FullRunState {
  chon: Record<string, boolean>;
  params: Record<string, ParamValues>;
}

export function khoiTao(steps: Step[], chuoiMacDinh: string[]): FullRunState {
  const chon: Record<string, boolean> = {};
  const params: Record<string, ParamValues> = {};
  for (const s of steps) {
    chon[s.id] = chuoiMacDinh.includes(s.id);
    params[s.id] = macDinh(s);
  }
  return { chon, params };
}

/** Nhung thu buoc dang bat can ma he thong chua co. */
export function thieuGi(steps: Step[], st: FullRunState, health: Health | null): string[] {
  if (!health) return [];
  const bat = steps.filter((s) => st.chon[s.id]);
  const ra: string[] = [];
  if (bat.some((s) => s.needs_chrome)) {
    if (!health.chrome.ok) ra.push("chưa tìm thấy Chrome trên máy chạy backend");
    if (health.toi && !health.toi.co_phien_panel)
      ra.push(
        "tài khoản của bạn chưa có phiên đăng nhập panel — vào mục Tài khoản để đăng nhập lại",
      );
  }
  if (bat.some((s) => s.needs_llm_key) && !health.api_key.ok)
    ra.push("chưa thêm API key nào ở mục API key");
  return ra;
}

/** Cac o bat buoc con bo trong, cua rieng nhung buoc dang bat. */
export function thieuThamSo(steps: Step[], st: FullRunState): string[] {
  const ra: string[] = [];
  for (const s of steps) {
    if (!st.chon[s.id]) continue;
    for (const p of s.params) {
      if (!p.required) continue;
      const v = st.params[s.id]?.[p.name];
      if (v === undefined || v === null || String(v).trim() === "")
        ra.push(`${s.label} → ${p.label}`);
    }
  }
  return ra;
}

export function coNguyHiem(steps: Step[], st: FullRunState): boolean {
  return steps.some(
    (s) => st.chon[s.id] && s.params.some((p) => p.danger && st.params[s.id]?.[p.name]),
  );
}

function KhoiBuoc({
  step,
  bat,
  values,
  onBat,
  onParams,
  mo,
  onMo,
  thuTu,
}: {
  step: Step;
  bat: boolean;
  values: ParamValues;
  onBat: (v: boolean) => void;
  onParams: (v: ParamValues) => void;
  mo: boolean;
  onMo: () => void;
  thuTu: number | null;
}) {
  return (
    <div
      className="card"
      style={{ marginBottom: 10, opacity: bat ? 1 : 0.55, boxShadow: "none" }}
    >
      <div className="card-head" style={{ padding: "10px 14px", cursor: "pointer" }}>
        <Switch checked={bat} onChange={onBat} />
        <span className="grow" onClick={onMo}>
          <span style={{ fontWeight: 600 }}>
            {thuTu !== null ? `#${thuTu} · ` : ""}
            {step.label}
          </span>
          {step.dangerous ? (
            <>
              {" "}
              <Badge tone="danger">Ghi thật</Badge>
            </>
          ) : null}
        </span>
        <button className="btn btn-sm btn-ghost" onClick={onMo}>
          {mo ? "▾" : "▸"}
        </button>
      </div>
      {mo ? (
        <div className="card-body" style={{ paddingTop: 12 }}>
          <div className="hint" style={{ marginTop: 0, marginBottom: 12 }}>
            {step.summary}
          </div>
          <ParamForm step={step} values={values} onChange={onParams} />

          {/* ParamForm co tinh bo qua tham so "danger" de khong ai bat nham.
              O day phai bu lai, neu khong buoc 6 nam trong chuoi se luon chay
              thu ma nguoi dung khong hieu tai sao khong ghi duoc gi. */}
          {step.params.some((p) => p.danger) ? (
            <div className="danger-zone" style={{ marginTop: 14 }}>
              <div className="dz-title">⚠ Ghi thật lên hệ thống</div>
              <div className="col" style={{ gap: 9 }}>
                {step.params
                  .filter((p) => p.danger)
                  .map((p) => (
                    <div key={p.name}>
                      <Switch
                        checked={Boolean(values[p.name])}
                        onChange={(v) => onParams({ ...values, [p.name]: v })}
                        label={p.label}
                      />
                      {p.help ? <div className="hint">{p.help}</div> : null}
                    </div>
                  ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function FullRunPanel({
  steps,
  state,
  onChange,
  health,
  confirmPhrase,
  confirm,
  onConfirm,
}: {
  steps: Step[];
  state: FullRunState;
  onChange: (s: FullRunState) => void;
  health: Health | null;
  confirmPhrase: string;
  confirm: string;
  onConfirm: (v: string) => void;
}) {
  // Mo san khoi dau tien dang bat - do thuong la cho phai dien link.
  const [mo, setMo] = useState<Record<string, boolean>>(() => {
    const dau = steps.find((s) => state.chon[s.id]);
    return dau ? { [dau.id]: true } : {};
  });

  const thieu = thieuGi(steps, state, health);
  const thieuTs = thieuThamSo(steps, state);
  const nguyHiem = coNguyHiem(steps, state);
  const soBat = steps.filter((s) => state.chon[s.id]).length;

  let thuTu = 0;

  return (
    <div>
      <Alert tone="info">
        Các bước chạy nối tiếp trong <strong>một</strong> lần chạy, log dồn chung một chỗ.
        Bước nào hỏng thì <strong>dừng cả chuỗi</strong> — các bước sau ăn theo đầu ra của
        bước trước, chạy tiếp chỉ sinh ra rác.
      </Alert>

      <div className="row" style={{ margin: "14px 0 10px" }}>
        <strong className="grow">Chọn các bước ({soBat} bước)</strong>
        <button
          className="btn btn-sm"
          onClick={() =>
            onChange({
              ...state,
              chon: Object.fromEntries(steps.map((s) => [s.id, !s.dangerous])),
            })
          }
        >
          Về mặc định
        </button>
      </div>

      {steps.map((s) => {
        if (state.chon[s.id]) thuTu += 1;
        return (
          <KhoiBuoc
            key={s.id}
            step={s}
            thuTu={state.chon[s.id] ? thuTu : null}
            bat={Boolean(state.chon[s.id])}
            values={state.params[s.id] ?? {}}
            mo={Boolean(mo[s.id])}
            onMo={() => setMo({ ...mo, [s.id]: !mo[s.id] })}
            onBat={(v) => onChange({ ...state, chon: { ...state.chon, [s.id]: v } })}
            onParams={(v) => onChange({ ...state, params: { ...state.params, [s.id]: v } })}
          />
        );
      })}

      {thieu.length > 0 ? (
        <div style={{ marginTop: 14 }}>
          <Alert tone="warn">
            <strong>Chuỗi này có thể hỏng vì:</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {thieu.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </Alert>
        </div>
      ) : null}

      {thieuTs.length > 0 ? (
        <div style={{ marginTop: 10 }}>
          <Alert tone="danger">
            <strong>Còn ô bắt buộc bỏ trống:</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {thieuTs.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </Alert>
        </div>
      ) : null}

      {nguyHiem ? (
        <div className="danger-zone" style={{ marginTop: 14 }}>
          <div className="dz-title">⚠ Chuỗi này có bước ghi thật — không hoàn tác được</div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="label">
              <span className="req">*</span>
              Gõ <code className="mono">{confirmPhrase}</code> để xác nhận
            </label>
            <input
              className="input"
              value={confirm}
              onChange={(e) => onConfirm(e.target.value)}
              placeholder={confirmPhrase}
              autoComplete="off"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
